import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { initDatafeedParams, Transaction } from "./initConfig/tradeView";
import { useWebSocket, useInterval, useUnmount } from "ahooks";
import { ReadyState } from "ahooks/lib/useWebSocket";
import dayjs from "dayjs";
import { WS_PREFIX, TRON_WS_PREFIX } from "./http";
import { TransSub } from "./initConfig/useTVChart";
import { v4 } from "uuid";
import { GloablMessageSub, GlobalMessageType } from "./rxjs";

function SocketSolana({
  handleMessage,
  chain,
  address,
}: {
  handleMessage: (v: WebSocketEventMap["message"]) => void;
  chain: initDatafeedParams["chain"];
  address: initDatafeedParams["address"];
}) {
  const [uuid, setUuid] = useState(v4());

  useEffect(() => {
    setUuid(v4());
  }, [address]);

  const { sendMessage, disconnect, readyState } = useWebSocket(
    `${WS_PREFIX}?clientId=${uuid}`,
    {
      reconnectLimit: 5,
      onClose: () => {
        if (readyState === ReadyState.Open) {
          sendMessage(
            JSON.stringify({
              cmd: "unsubscribeInfo",
              clientId: uuid,
            })
          );
        }
      },
      onError: (err) => {},
      onMessage: handleMessage,
      onOpen: () => {},
    }
  );

  useInterval(() => {
    if (readyState === ReadyState.Open) {
      sendMessage("ping");
    }
  }, 5 * 1000);

  useEffect(() => {
    if (chain.toLowerCase() === "solana") {
      if (readyState !== ReadyState.Open || !address) {
        return;
      }
      sendMessage(
        JSON.stringify({
          at: Date.now(),
          cmd: "subscribeInfo",
          module: "Token",
          chain: chain,
          clientId: uuid,
          data: {
            token: address,
            res: ["price", "latestTransaction"],
          },
        })
      );
    } else {
    }
  }, [readyState, chain, address]);

  useUnmount(() => {
    disconnect();
  });

  return <></>;
}

function SocketTron({
  handleMessage,
  chain,
  address,
}: {
  handleMessage: (v: WebSocketEventMap["message"]) => void;
  chain: initDatafeedParams["chain"];
  address: initDatafeedParams["address"];
}) {
  const [uuid, setUuid] = useState(v4());

  useEffect(() => {
    setUuid(v4());
  }, [address]);

  const {
    sendMessage: sendTronMessage,
    disconnect: disconnectTron,
    readyState: tronReadyState,
  } = useWebSocket(`${TRON_WS_PREFIX}?clientId=${uuid}`, {
    reconnectLimit: Infinity,
    reconnectInterval: 1000,
    onClose: () => {
      if (tronReadyState === ReadyState.Open) {
        sendTronMessage(
          JSON.stringify({
            cmd: "unsubscribeInfo",
            clientId: uuid,
          })
        );
      }
    },
    onError: (err) => {},
    onMessage: handleMessage,
  });

  useInterval(() => {
    if (tronReadyState === ReadyState.Open) {
      sendTronMessage("ping");
    }
  }, 5 * 1000);

  useEffect(() => {
    if (chain.toLowerCase() === "tron") {
      if (tronReadyState !== ReadyState.Open || !address) {
        return;
      }
      sendTronMessage(
        JSON.stringify({
          at: Date.now(),
          cmd: "subscribeInfo",
          module: "Token",
          chain: chain,
          clientId: uuid,
          data: {
            token: address,
            res: ["price", "latestTransaction"],
          },
        })
      );
    }
  }, [tronReadyState, chain, address]);

  useUnmount(() => {
    disconnectTron();
  });

  return <></>;
}

export default function Socket({
  chain = "solana",
  address,
  walletAddressRef,
}: {
  chain: string;
  address: string;
  walletAddressRef: RefObject<string>;
}) {
  const handleMessage = useCallback(
    (dataRaw: WebSocketEventMap["message"]) => {
      try {
        const data: Transaction[] = JSON.parse(dataRaw.data) || [];
        // 可以考虑通过postMessage将socket数据传递出去
        if (Array.isArray(data)) {
          const filtered = data.filter((i) => i.txID && i.token === address);
          if (!filtered.length) return;
          filtered.forEach((i) => {
            // 服务器默认+8时区
            i.unix = dayjs.tz(i.timedate).unix();
            if (i.owner === walletAddressRef.current) {
              GloablMessageSub.next({
                type: GlobalMessageType.WATCH_WALLET_TRADE_CHANGE,
                data: i,
              });
            }
          });
          filtered.reverse();
          TransSub.next(filtered);
          // window.parent.postMessage(
          //   {
          //     type: "SPTKLINE_TRANSACTION_UPDATE",
          //     value: JSON.stringify(filtered),
          //   },
          //   "*"
          // );
        }
      } catch {}
    },
    [address]
  );

  return chain.toLowerCase() === "tron" ? (
    <SocketTron handleMessage={handleMessage} chain={chain} address={address} />
  ) : (
    <SocketSolana
      handleMessage={handleMessage}
      chain={chain}
      address={address}
    />
  );
}
